import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PromangRichTextEditor } from "../../utils/PromangRichTextEditor";
import { DirectImageUploadButton } from "../Media/DirectImageUploadButton";
import { trainingApi } from "../../../api/training";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import TrainingPlayer from "./TrainingPlayer";
import TrainingGuideJsonDialog from "./TrainingGuideJsonDialog";
import TrainingTranslationsStep from "./TrainingTranslationsStep";
import { applyTrainingGuideContent } from "./trainingGuideJson";
import {
  buttonClass,
  primaryClass,
  inputClass,
  panelClass,
  Field,
  Feedback,
  RemoteOptions,
  initialDraft,
  draftFromGuide,
  newCard,
  newKey,
  validateDraft,
  guidePayload,
  useAlive,
} from "./trainingAdminShared";

const steps = [
  "trainingAdmin.about",
  "trainingAdmin.content",
  "trainingAdmin.languages",
  "trainingAdmin.audience",
  "trainingAdmin.followup",
  "trainingAdmin.review",
];
const typeLabels = {
  content: "trainingAdmin.contentType",
  acknowledgement: "trainingAdmin.acknowledgementType",
  single_choice: "trainingAdmin.singleType",
  multiple_choice: "trainingAdmin.multipleType",
  document: "trainingAdmin.documentType",
};
function move(rows, index, delta) {
  const next = [...rows];
  [next[index], next[index + delta]] = [next[index + delta], next[index]];
  return next;
}

function CardEditor({
  card,
  update,
  remove,
  moveUp,
  moveDown,
  guideId,
  allCards,
  disabled,
}) {
  const { t } = useTranslation();
  const question = ["single_choice", "multiple_choice"].includes(card.type);
  const [documentPicker, setDocumentPicker] = useState(false);
  const toggleCorrect = (id) =>
    update({
      correct_option_ids:
        card.type === "single_choice"
          ? [id]
          : card.correct_option_ids.includes(id)
            ? card.correct_option_ids.filter((value) => value !== id)
            : [...card.correct_option_ids, id],
    });
  return (
    <section
      className={`${panelClass} space-y-5`}
      aria-label={card.title || t("trainingAdmin.card")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
          {t(typeLabels[card.type])}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            className={buttonClass}
            disabled={!moveUp || disabled}
            onClick={moveUp}
            aria-label={t("trainingAdmin.moveUp")}
          >
            ↑
          </button>
          <button
            type="button"
            className={buttonClass}
            disabled={!moveDown || disabled}
            onClick={moveDown}
            aria-label={t("trainingAdmin.moveDown")}
          >
            ↓
          </button>
          <button
            type="button"
            className={`${buttonClass} !text-rose-700`}
            disabled={disabled}
            onClick={remove}
          >
            {t("trainingAdmin.remove")}
          </button>
        </div>
      </div>
      <Field label={t("trainingAdmin.cardTitle")}>
        <input
          className={inputClass}
          value={card.title}
          onChange={(event) => update({ title: event.target.value })}
          maxLength={300}
        />
      </Field>
      <Field label={t("trainingAdmin.cardType")}>
        <select
          className={inputClass}
          value={card.type}
          onChange={(event) =>
            update({
              type: event.target.value,
              correct_option_ids: [],
              document_required: event.target.value === "document",
            })
          }
        >
          {Object.entries(typeLabels).map(([value, key]) => (
            <option key={value} value={value}>
              {t(key)}
            </option>
          ))}
        </select>
      </Field>
      <div className="space-y-2">
        <div className="text-sm font-semibold text-slate-700">
          {t("trainingAdmin.body")}
        </div>
        <PromangRichTextEditor
          value={card.body}
          onChange={(body) => update({ body })}
          disabled={disabled}
          templatesEnabled={false}
          tablesEnabled={false}
          size="sm"
          contextMenu={{ insertImage: false }}
        />
      </div>
      <label className="flex min-h-[44px] items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={card.required}
          onChange={(event) => update({ required: event.target.checked })}
        />
        {t("trainingAdmin.required")}
      </label>
      {question && (
        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-semibold">
            {t("trainingAdmin.options")}
          </legend>
          {card.options.map((option, index) => (
            <div key={option.id} className="flex items-center gap-2">
              <label className="flex min-h-[44px] items-center gap-2 text-xs">
                <input
                  aria-label={`${t("trainingAdmin.correct")} ${index + 1}`}
                  type={card.type === "single_choice" ? "radio" : "checkbox"}
                  checked={card.correct_option_ids.includes(option.id)}
                  onChange={() => toggleCorrect(option.id)}
                />
                <span>{t("trainingAdmin.correct")}</span>
              </label>
              <input
                className={inputClass}
                aria-label={`${t("trainingAdmin.response")} ${index + 1}`}
                value={option.text}
                maxLength={1000}
                onChange={(event) =>
                  update({
                    options: card.options.map((row) =>
                      row.id === option.id
                        ? { ...row, text: event.target.value }
                        : row,
                    ),
                  })
                }
              />
              <button
                type="button"
                className={buttonClass}
                aria-label={`${t("trainingAdmin.remove")} ${index + 1}`}
                disabled={card.options.length <= 2}
                onClick={() =>
                  update({
                    options: card.options.filter((row) => row.id !== option.id),
                    correct_option_ids: card.correct_option_ids.filter(
                      (id) => id !== option.id,
                    ),
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className={buttonClass}
            disabled={card.options.length >= 20}
            onClick={() =>
              update({ options: [...card.options, { id: newKey(), text: "" }] })
            }
          >
            {t("trainingAdmin.addOption")}
          </button>
          <Field label={t("trainingAdmin.explanation")}>
            <textarea
              className={inputClass}
              rows={3}
              value={card.explanation}
              onChange={(event) => update({ explanation: event.target.value })}
            />
          </Field>
        </fieldset>
      )}
      <div className="space-y-3 border-t border-slate-100 pt-4">
        {guideId ? (
          <DirectImageUploadButton
            module="training_guide"
            moduleParam={guideId}
            disabled={disabled}
            onUploadComplete={(documents) => {
              if (documents[0]?.id)
                update({ image_document_id: documents[0].id });
            }}
          />
        ) : (
          <p className="text-xs text-slate-500">
            {t("trainingAdmin.saveBeforeImage")}
          </p>
        )}
        {card.image_document_id && (
          <div className="space-y-2">
            <p className="text-sm text-emerald-700">
              {t("trainingAdmin.imageReady")} #{card.image_document_id}
            </p>
            <Field label={t("trainingAdmin.imageAlt")}>
              <input
                className={inputClass}
                value={card.image_alt}
                onChange={(event) => update({ image_alt: event.target.value })}
              />
            </Field>
            <button
              type="button"
              className={buttonClass}
              onClick={() => update({ image_document_id: null, image_alt: "" })}
            >
              {t("trainingAdmin.remove")}
            </button>
          </div>
        )}
      </div>
      <div className="space-y-3 border-t border-slate-100 pt-4">
        <button
          type="button"
          className={buttonClass}
          onClick={() => setDocumentPicker(!documentPicker)}
        >
          {t("trainingAdmin.document")}
          {card.document_version_id
            ? ` · ${card.document?.title || `#${card.document_version_id}`}`
            : ""}
        </button>
        {card.document_version_id && (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span>
              {t("trainingAdmin.boundVersion")}:{" "}
              {card.document?.version || card.document_version_id}
            </span>
            <button
              type="button"
              className={buttonClass}
              onClick={() =>
                update({
                  document_version_id: null,
                  document: null,
                  document_required: false,
                })
              }
            >
              {t("trainingAdmin.remove")}
            </button>
          </div>
        )}
        {documentPicker && (
          <RemoteOptions
            kind="documents"
            label={t("trainingAdmin.documentSearch")}
            selected={
              card.document_version_id ? [card.document_version_id] : []
            }
            multiple={false}
            onPick={(document) => {
              update({ document_version_id: document.id, document });
              setDocumentPicker(false);
            }}
            renderLabel={(row) =>
              `${row.document_number ? `${row.document_number} · ` : ""}${row.title} · ${t("trainingAdmin.revision")} ${row.version}`
            }
          />
        )}
        {card.document_version_id && (card.document_required || card.type === "document") && (
          <p className="text-[13px] leading-5 text-slate-600">{t("trainingAdmin.documentAccessHint")}</p>
        )}
        {card.document_version_id && (
          <label className="flex min-h-[44px] items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={card.document_required}
              onChange={(event) =>
                update({ document_required: event.target.checked })
              }
            />
            {t("trainingAdmin.readRequired")}
          </label>
        )}
      </div>
      {question && allCards.length > 1 && (
        <fieldset className="space-y-2 border-t border-slate-100 pt-4">
          <legend className="text-sm font-semibold">
            {t("trainingAdmin.dependency")}
          </legend>
          {allCards
            .filter((row) => row.key !== card.key)
            .map((row) => (
              <label
                key={row.key}
                className="flex min-h-[44px] items-center gap-3 text-sm"
              >
                <input
                  type="checkbox"
                  checked={card.depends_on.includes(row.key)}
                  onChange={(event) =>
                    update({
                      depends_on: event.target.checked
                        ? [...card.depends_on, row.key]
                        : card.depends_on.filter((key) => key !== row.key),
                    })
                  }
                />
                {row.title || t(typeLabels[row.type])}
              </label>
            ))}
        </fieldset>
      )}
    </section>
  );
}

export default function TrainingGuideEditor({
  guide: initialGuide,
  open = true,
  onClose,
  onSaved,
  contextKey,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() =>
    initialGuide ? draftFromGuide(initialGuide) : initialDraft(),
  );
  const [baseline, setBaseline] = useState(() =>
    initialGuide ? JSON.stringify(draftFromGuide(initialGuide)) : null,
  );
  const [step, setStep] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [validation, setValidation] = useState([]);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [serverGuide, setServerGuide] = useState(null);
  const [impact, setImpact] = useState(null);
  const [audience, setAudience] = useState(null);
  const [acceptedImpact, setAcceptedImpact] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewSize, setPreviewSize] = useState("desktop");
  const [jsonOpen, setJsonOpen] = useState(false);
  const pending = useRef(null);
  const alive = useAlive();
  const dirty = baseline !== JSON.stringify(draft);
  const frozen =
    busy ||
    Boolean(
      error &&
      (error.httpStatus === 409 ||
        (pending.current && (!error.httpStatus || error.httpStatus >= 500))),
    );
  const patchGuide = (patch) => {
    setDraft((old) => ({ ...old, guide: { ...old.guide, ...patch } }));
    setImpact(null);
    setMessage(null);
  };
  const patchRevision = (patch) => {
    setDraft((old) => ({ ...old, revision: { ...old.revision, ...patch } }));
    setImpact(null);
    setMessage(null);
  };
  const patchPolicy = (patch) =>
    patchRevision({ policy: { ...draft.revision.policy, ...patch } });
  const pages = draft.revision.pages;
  const page = pages[Math.min(pageIndex, pages.length - 1)];
  const allCards = pages.flatMap((row) => row.cards);
  const setPage = (patch) =>
    patchRevision({
      pages: pages.map((row) =>
        row.key === page.key ? { ...row, ...patch } : row,
      ),
    });
  const removeCard = (key) =>
    patchRevision({
      pages: pages.map((row) => ({
        ...row,
        cards: row.cards
          .filter((card) => card.key !== key)
          .map((card) => ({
            ...card,
            depends_on: (card.depends_on || []).filter(
              (dependency) => dependency !== key,
            ),
          })),
      })),
    });
  const adopt = (guide) => {
    const next = draftFromGuide(guide);
    setDraft(next);
    setBaseline(JSON.stringify(next));
    setImpact(null);
    setAcceptedImpact(false);
  };

  // Persisted request identity survives an uncertain response. A status check reuses
  // this exact request; the API's idempotency contract prevents duplicate effects.
  const executeMutation = async (operation) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    pending.current = operation;
    try {
      const result = await operation.run();
      if (!alive.current) return;
      adopt(result.data);
      pending.current = null;
      setMessage(t(operation.success));
      try {
        await onSaved?.(result.data);
      } catch {
        if (alive.current)
          setMessage(
            `${t(operation.success)}. ${t("trainingAdmin.refreshWarning")}`,
          );
      }
    } catch (e) {
      if (alive.current) {
        const failure = normalizeError(e);
        if (failure.httpStatus >= 400 && failure.httpStatus < 500)
          pending.current = null;
        setError(failure);
      }
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const save = () => {
    if (!draft.guide.title.trim()) {
      setValidation(["trainingAdmin.validationTitle"]);
      setStep(0);
      return;
    }
    setValidation([]);
    const payload = { ...guidePayload(draft), request_key: newKey() };
    executeMutation({
      run: () =>
        draft.guide.id
          ? trainingApi.updateGuide(draft.guide.id, payload)
          : trainingApi.createGuide(payload),
      success: "trainingAdmin.saved",
    });
  };
  const review = async () => {
    const errors = validateDraft(draft);
    setValidation(errors);
    if (errors.length || dirty || !draft.guide.id || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const [diff, users] = await Promise.all([
        trainingApi.revisionDiff(draft.guide.id),
        trainingApi.audiencePreview(draft.guide.id, { limit: 100 }),
      ]);
      if (alive.current) {
        setImpact(diff.data);
        setAudience(users);
        setAcceptedImpact(false);
      }
    } catch (e) {
      if (alive.current) setError(normalizeError(e));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const loadAudiencePage = async (offset) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const result = await trainingApi.audiencePreview(draft.guide.id, {
        limit: 100,
        offset,
      });
      if (alive.current) setAudience(result);
    } catch (e) {
      if (alive.current) setError(normalizeError(e));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const preview = async () => {
    // Unsaved draft can be previewed without creating real assignments or answers.
    if (!draft.guide.id) {
      setPreviewData({ guide: draft.guide, revision: draft.revision });
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const result = await trainingApi.preview(
        draft.guide.id,
        dirty
          ? {
              guide: guidePayload(draft).guide,
              revision: guidePayload(draft).revision,
            }
          : undefined,
      );
      if (alive.current)
        setPreviewData({
          ...result.data,
          guide: {
            ...result.data.guide,
            title: draft.guide.title,
            description: draft.guide.description,
          },
        });
    } catch (e) {
      if (alive.current) setError(normalizeError(e));
    } finally {
      busyRef.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const publish = () => {
    if (!impact || !acceptedImpact || dirty || impact.access_issues?.length)
      return;
    const payload = {
      expected_version: draft.guide.lock_version,
      request_key: newKey(),
    };
    executeMutation({
      run: () => trainingApi.publish(draft.guide.id, payload),
      success: "trainingAdmin.published",
    });
  };
  const loadServer = async () => {
    setBusy(true);
    try {
      const result = await trainingApi.guide(draft.guide.id);
      if (alive.current) setServerGuide(result.data);
    } catch (e) {
      if (alive.current) setError(normalizeError(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const requestClose = () => {
    if (busy) return;
    if (dirty || pending.current) setDiscardOpen(true);
    else onClose();
  };
  const notification = draft.guide.notification_settings;
  const policy = draft.revision.policy;
  const published = Boolean(
    draft.guide.current_revision_id &&
    !draft.guide.draft_revision &&
    draft.revision.state === "published",
  );
  return (
    <>
      <PMDialog
        open={open && !previewData && !jsonOpen}
        onClose={requestClose}
        title={t("trainingAdmin.title")}
        dense
        closeOnOutsideClick
        panelClassName="!max-w-6xl sm:!h-[820px] sm:!max-h-[90vh]"
        contentClassName="!overflow-hidden"
      >
        {discardOpen ? (
          <div className="space-y-5 p-6">
            <h2 className="text-xl font-bold">
              {t("trainingAdmin.leaveQuestion")}
            </h2>
            {pending.current && (
              <p className="text-sm">{t("trainingAdmin.unknown")}</p>
            )}
            <div className="flex flex-wrap gap-3">
              <button
                className={buttonClass}
                onClick={() => setDiscardOpen(false)}
              >
                {t("trainingAdmin.keepEditing")}
              </button>
              <button className={primaryClass} onClick={onClose}>
                {t("trainingAdmin.discard")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="shrink-0 border-b border-slate-200 px-3 pb-3 sm:px-2">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="truncate text-lg font-bold text-slate-900">
                  {draft.guide.title || t("trainingAdmin.newGuide")}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className={buttonClass}
                    disabled={frozen}
                    onClick={() => setJsonOpen(true)}
                  >
                    {t("trainingAdmin.jsonTools")}
                  </button>
                  <span className="text-xs text-slate-500">
                    {t("trainingAdmin.revision")} {draft.revision.number || 1}
                  </span>
                </div>
              </div>
              <nav
                className="flex gap-1 overflow-x-auto"
                aria-label={t("trainingAdmin.edit")}
              >
                {steps.map((key, index) => (
                  <button
                    type="button"
                    key={key}
                    className={`min-h-[44px] shrink-0 rounded-full px-3 text-xs font-semibold ${step === index ? "bg-[#1463C7] text-white" : "bg-slate-100 text-slate-600"}`}
                    aria-current={step === index ? "step" : undefined}
                    onClick={() => setStep(index)}
                  >
                    {index + 1}. {t(key)}
                  </button>
                ))}
              </nav>
            </div>
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-slate-50 p-4 sm:p-6">
              <Feedback error={error} message={message}>
                {error?.httpStatus === 409 && draft.guide.id && (
                  <button
                    className={`${buttonClass} mt-3`}
                    onClick={loadServer}
                    disabled={busy}
                  >
                    {t("trainingAdmin.loadServer")}
                  </button>
                )}
                {error &&
                  (!error.httpStatus || error.httpStatus >= 500) &&
                  pending.current && (
                    <button
                      className={`${buttonClass} mt-3`}
                      onClick={() => executeMutation(pending.current)}
                      disabled={busy}
                    >
                      {t("trainingAdmin.checkStatus")}
                    </button>
                  )}
              </Feedback>
              {serverGuide && (
                <section className={`${panelClass} space-y-3`}>
                  <h3 className="font-bold">
                    {t("trainingAdmin.serverVersion")}: {serverGuide.title}
                  </h3>
                  <p className="text-sm">{serverGuide.description}</p>
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={() =>
                      setPreviewData({
                        guide: serverGuide,
                        revision:
                          serverGuide.draft_revision ||
                          serverGuide.current_revision,
                      })
                    }
                  >
                    {t("trainingAdmin.preview")}
                  </button>
                  <button
                    className={buttonClass}
                    onClick={() => {
                      adopt(serverGuide);
                      setServerGuide(null);
                      setError(null);
                      pending.current = null;
                    }}
                  >
                    {t("trainingAdmin.useServer")}
                  </button>
                </section>
              )}
              {validation.length > 0 && (
                <ul
                  role="alert"
                  className="space-y-1 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
                >
                  {validation.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              )}
              <fieldset
                disabled={frozen || published}
                className="min-w-0 space-y-5"
              >
                {step === 0 && (
                  <section className={`${panelClass} space-y-5`}>
                    <Field label={t("trainingAdmin.name")}>
                      <input
                        autoFocus
                        className={inputClass}
                        value={draft.guide.title}
                        maxLength={200}
                        onChange={(event) =>
                          patchGuide({ title: event.target.value })
                        }
                      />
                    </Field>
                    <Field label={t("trainingAdmin.purpose")}>
                      <textarea
                        className={inputClass}
                        rows={4}
                        value={draft.guide.description}
                        maxLength={4000}
                        onChange={(event) =>
                          patchGuide({ description: event.target.value })
                        }
                      />
                    </Field>
                    <Field label={t("trainingAdmin.minutes")}>
                      <input
                        className={inputClass}
                        type="number"
                        min="1"
                        max="480"
                        value={policy.estimated_minutes}
                        onChange={(event) =>
                          patchPolicy({
                            estimated_minutes: Number(event.target.value),
                          })
                        }
                      />
                    </Field>
                    <RemoteOptions
                      kind="users"
                      label={t("trainingAdmin.owner")}
                      selected={
                        draft.guide.responsible_user_id
                          ? [draft.guide.responsible_user_id]
                          : []
                      }
                      multiple={false}
                      onChange={(ids) =>
                        patchGuide({ responsible_user_id: ids[0] })
                      }
                    />
                    <Field label={t("trainingAdmin.changeNote")}>
                      <textarea
                        className={inputClass}
                        rows={2}
                        value={draft.revision.change_summary}
                        onChange={(event) =>
                          patchRevision({ change_summary: event.target.value })
                        }
                      />
                    </Field>
                  </section>
                )}
                {step === 1 && (
                  <div className="grid items-start gap-5 md:grid-cols-[210px_minmax(0,1fr)]">
                    <aside
                      className={`${panelClass} space-y-2 !p-3 md:sticky md:top-0`}
                    >
                      {pages.map((row, index) => (
                        <button
                          type="button"
                          className={`min-h-[44px] w-full rounded-lg px-3 py-2 text-left text-sm ${index === pageIndex ? "bg-blue-50 font-semibold text-blue-800" : "text-slate-600 hover:bg-slate-50"}`}
                          key={row.key}
                          onClick={() => setPageIndex(index)}
                        >
                          {index + 1}. {row.title || t("trainingAdmin.page")}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`${buttonClass} w-full`}
                        disabled={pages.length >= 50}
                        onClick={() => {
                          patchRevision({
                            pages: [
                              ...pages,
                              { key: newKey(), title: "", cards: [] },
                            ],
                          });
                          setPageIndex(pages.length);
                        }}
                      >
                        {t("trainingAdmin.addPage")}
                      </button>
                    </aside>
                    <div className="min-w-0 space-y-4">
                      <div className={`${panelClass} space-y-3`}>
                        <Field label={t("trainingAdmin.pageTitle")}>
                          <input
                            className={inputClass}
                            value={page.title}
                            onChange={(event) =>
                              setPage({ title: event.target.value })
                            }
                          />
                        </Field>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className={buttonClass}
                            disabled={pageIndex === 0}
                            onClick={() => {
                              patchRevision({
                                pages: move(pages, pageIndex, -1),
                              });
                              setPageIndex(pageIndex - 1);
                            }}
                          >
                            {t("trainingAdmin.moveUp")}
                          </button>
                          <button
                            type="button"
                            className={buttonClass}
                            disabled={pageIndex === pages.length - 1}
                            onClick={() => {
                              patchRevision({
                                pages: move(pages, pageIndex, 1),
                              });
                              setPageIndex(pageIndex + 1);
                            }}
                          >
                            {t("trainingAdmin.moveDown")}
                          </button>
                          <button
                            type="button"
                            className={buttonClass}
                            disabled={pages.length === 1}
                            onClick={() => {
                              const removedKeys = page.cards.map(
                                (card) => card.key,
                              );
                              patchRevision({
                                pages: pages
                                  .filter((row) => row.key !== page.key)
                                  .map((row) => ({
                                    ...row,
                                    cards: row.cards.map((card) => ({
                                      ...card,
                                      depends_on: card.depends_on.filter(
                                        (key) => !removedKeys.includes(key),
                                      ),
                                    })),
                                  })),
                              });
                              setPageIndex(Math.max(0, pageIndex - 1));
                            }}
                          >
                            {t("trainingAdmin.remove")}
                          </button>
                        </div>
                      </div>
                      {page.cards.map((card, index) => (
                        <CardEditor
                          key={card.key}
                          card={card}
                          guideId={draft.guide.id}
                          disabled={frozen}
                          allCards={allCards}
                          update={(patch) => {
                            setDraft((old) => ({
                              ...old,
                              revision: {
                                ...old.revision,
                                pages: old.revision.pages.map((savedPage) => ({
                                  ...savedPage,
                                  cards: savedPage.cards.map((row) =>
                                    row.key === card.key
                                      ? { ...row, ...patch }
                                      : row,
                                  ),
                                })),
                              },
                            }));
                            setImpact(null);
                            setMessage(null);
                          }}
                          remove={() => removeCard(card.key)}
                          moveUp={
                            index > 0
                              ? () =>
                                  setPage({
                                    cards: move(page.cards, index, -1),
                                  })
                              : null
                          }
                          moveDown={
                            index < page.cards.length - 1
                              ? () =>
                                  setPage({ cards: move(page.cards, index, 1) })
                              : null
                          }
                        />
                      ))}
                      <button
                        type="button"
                        className={`${buttonClass} w-full border-dashed`}
                        disabled={allCards.length >= 200}
                        onClick={() =>
                          setPage({ cards: [...page.cards, newCard()] })
                        }
                      >
                        + {t("trainingAdmin.addCard")}
                      </button>
                    </div>
                  </div>
                )}
                {step === 2 && (
                  <TrainingTranslationsStep
                    draft={draft}
                    patchRevision={patchRevision}
                    disabled={frozen}
                  />
                )}
                {step === 3 && (
                  <section className={`${panelClass} space-y-5`}>
                    <Field
                      label={t("trainingAdmin.audience")}
                      hint={t("trainingAdmin.audienceHint")}
                    >
                      <select
                        className={inputClass}
                        value={policy.audience}
                        onChange={(event) =>
                          patchPolicy({ audience: event.target.value })
                        }
                      >
                        <option value="all">
                          {t("trainingAdmin.allUsers")}
                        </option>
                        <option value="positions">
                          {t("trainingAdmin.positions")}
                        </option>
                        <option value="users">
                          {t("trainingAdmin.namedUsers")}
                        </option>
                      </select>
                    </Field>
                    {policy.audience === "positions" && (
                      <RemoteOptions
                        kind="positions"
                        label={t("trainingAdmin.positions")}
                        selected={policy.position_ids}
                        onChange={(position_ids) =>
                          patchPolicy({ position_ids })
                        }
                      />
                    )}
                    {policy.audience === "users" && (
                      <RemoteOptions
                        kind="users"
                        label={t("trainingAdmin.namedUsers")}
                        selected={policy.user_ids}
                        onChange={(user_ids) => patchPolicy({ user_ids })}
                      />
                    )}
                    <Field label={t("trainingAdmin.requirement")}>
                      <select
                        className={inputClass}
                        value={policy.requirement}
                        onChange={(event) =>
                          patchPolicy({
                            requirement: event.target.value,
                            mandatory_new_users_only: event.target.value === "optional" ? false : policy.mandatory_new_users_only,
                            allow_snooze: event.target.value !== "immediate",
                          })
                        }
                      >
                        <option value="optional">
                          {t("trainingAdmin.optional")}
                        </option>
                        <option value="deadline">
                          {t("trainingAdmin.deadline")}
                        </option>
                        <option value="immediate">
                          {t("trainingAdmin.immediate")}
                        </option>
                      </select>
                    </Field>
                    <label className="flex min-h-[44px] items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={policy.mandatory_new_users_only === true}
                        disabled={policy.requirement === "optional"}
                        onChange={(event) => patchPolicy({ mandatory_new_users_only: event.target.checked })}
                      />
                      <span>
                        <span className="font-semibold">{t("trainingAdmin.newUsersOnly")}</span>
                        <span className="mt-1 block text-slate-600">{t("trainingAdmin.newUsersOnlyHint")}</span>
                      </span>
                    </label>
                    {policy.requirement === "immediate" && (
                      <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
                        {t(policy.mandatory_new_users_only ? "trainingAdmin.newUsersImmediateWarning" : "trainingAdmin.immediateWarning")}
                      </p>
                    )}
                    {policy.requirement === "deadline" && (
                      <>
                        <Field label={t("trainingAdmin.deadlineType")}>
                          <select
                            className={inputClass}
                            value={
                              policy.due_days == null ? "fixed" : "relative"
                            }
                            onChange={(event) =>
                              patchPolicy(
                                event.target.value === "fixed"
                                  ? { due_days: null, due_at: "" }
                                  : { due_days: 7, due_at: null },
                              )
                            }
                          >
                            <option value="relative">
                              {t("trainingAdmin.relativeDays")}
                            </option>
                            <option value="fixed">
                              {t("trainingAdmin.fixedDate")}
                            </option>
                          </select>
                        </Field>
                        {policy.due_days != null ? (
                          <Field label={t("trainingAdmin.dueDays")}>
                            <input
                              className={inputClass}
                              type="number"
                              min="1"
                              max="3650"
                              value={policy.due_days}
                              onChange={(event) =>
                                patchPolicy({
                                  due_days: Number(event.target.value),
                                })
                              }
                            />
                          </Field>
                        ) : (
                          <Field label={t("trainingAdmin.dueAt")}>
                            <input
                              className={inputClass}
                              type="datetime-local"
                              value={
                                policy.due_at
                                  ? new Date(
                                      new Date(policy.due_at).getTime() -
                                        new Date(
                                          policy.due_at,
                                        ).getTimezoneOffset() *
                                          60000,
                                    )
                                      .toISOString()
                                      .slice(0, 16)
                                  : ""
                              }
                              onChange={(event) =>
                                patchPolicy({
                                  due_at: event.target.value
                                    ? new Date(event.target.value).toISOString()
                                    : "",
                                })
                              }
                            />
                          </Field>
                        )}
                        {policy.due_at &&
                          new Date(policy.due_at) < new Date() && (
                            <p className="text-sm text-amber-800">
                              {t("trainingAdmin.overdueWarning")}
                            </p>
                          )}
                      </>
                    )}
                    {policy.requirement !== "optional" && (
                      <label className="flex min-h-[44px] items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm">
                        <input type="checkbox" className="mt-1" checked={policy.allow_mandatory_snooze === true}
                          onChange={(event) => patchPolicy({ allow_mandatory_snooze: event.target.checked })} />
                        <span><span className="font-semibold">{t("trainingAdmin.mandatorySnooze")}</span>
                          <span className="mt-1 block text-slate-600">{t("trainingAdmin.mandatorySnoozeHint")}</span></span>
                      </label>
                    )}
                    {policy.requirement !== "immediate" && (
                      <label className="flex min-h-[44px] items-center gap-3 text-sm">
                        <input
                          type="checkbox"
                          checked={policy.allow_snooze}
                          onChange={(event) =>
                            patchPolicy({ allow_snooze: event.target.checked })
                          }
                        />
                        {t(policy.requirement === "deadline" ? "trainingAdmin.snoozeBeforeDeadline" : "trainingAdmin.snooze")}
                      </label>
                    )}
                  </section>
                )}
                {step === 4 && (
                  <section className={`${panelClass} space-y-5`}>
                    <RemoteOptions
                      kind="users"
                      label={t("trainingAdmin.owner")}
                      selected={
                        draft.guide.responsible_user_id
                          ? [draft.guide.responsible_user_id]
                          : []
                      }
                      multiple={false}
                      onChange={(ids) =>
                        patchGuide({ responsible_user_id: ids[0] })
                      }
                    />
                    <label className="flex min-h-[44px] items-center gap-3 text-sm font-semibold">
                      <input
                        type="checkbox"
                        checked={notification.enabled}
                        onChange={(event) =>
                          patchGuide({
                            notification_settings: {
                              ...notification,
                              enabled: event.target.checked,
                            },
                          })
                        }
                      />
                      {t("trainingAdmin.emailOptIn")}
                    </label>
                    <p className="text-sm text-slate-500">
                      {t("trainingAdmin.emailHint")}
                    </p>
                    {notification.enabled && (
                      <>
                        <Field label={t("trainingAdmin.cadence")}>
                          <select
                            className={inputClass}
                            value={notification.mode}
                            onChange={(event) =>
                              patchGuide({
                                notification_settings: {
                                  ...notification,
                                  mode: event.target.value,
                                },
                              })
                            }
                          >
                            <option value="daily">
                              {t("trainingAdmin.daily")}
                            </option>
                            <option value="weekly">
                              {t("trainingAdmin.weekly")}
                            </option>
                            <option value="events">
                              {t("trainingAdmin.events")}
                            </option>
                          </select>
                        </Field>
                        {[
                          ["completed", "trainingAdmin.completed"],
                          ["overdue", "trainingAdmin.overdue"],
                          ["blocked", "trainingAdmin.blocked"],
                        ].map(([event, label]) => (
                          <label
                            key={event}
                            className="flex min-h-[44px] items-center gap-3 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={notification.events.includes(event)}
                              onChange={(change) =>
                                patchGuide({
                                  notification_settings: {
                                    ...notification,
                                    events: change.target.checked
                                      ? [...notification.events, event]
                                      : notification.events.filter(
                                          (value) => value !== event,
                                        ),
                                  },
                                })
                              }
                            />
                            {t(label)}
                          </label>
                        ))}
                      </>
                    )}
                  </section>
                )}
              </fieldset>
              {step === 5 && (
                <div className="space-y-5">
                  <section className={`${panelClass} space-y-4`}>
                    <h3 className="text-lg font-bold">
                      {t("trainingAdmin.preview")}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {t("trainingAdmin.previewHint")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <select
                        className={inputClass + " !w-auto"}
                        aria-label={t("trainingAdmin.preview")}
                        value={previewSize}
                        onChange={(event) => setPreviewSize(event.target.value)}
                      >
                        <option value="desktop">
                          {t("trainingAdmin.desktop")}
                        </option>
                        <option value="mobile">
                          {t("trainingAdmin.mobile")}
                        </option>
                      </select>
                      <button
                        type="button"
                        className={buttonClass}
                        onClick={preview}
                      >
                        {t("trainingAdmin.preview")}
                      </button>
                    </div>
                  </section>
                  {!published && (
                    <section className={`${panelClass} space-y-4`}>
                      <h3 className="text-lg font-bold">
                        {t("trainingAdmin.impact")}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {t("trainingAdmin.publishInfo")}
                      </p>
                      {dirty && (
                        <p className="text-sm text-amber-800">
                          {t("trainingAdmin.saveForReview")}
                        </p>
                      )}
                      <button
                        type="button"
                        className={buttonClass}
                        disabled={dirty || frozen || !draft.guide.id}
                        onClick={review}
                      >
                        {t("trainingAdmin.reviewImpact")}
                      </button>
                      {impact && (
                        <>
                          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {[
                              [
                                "trainingAdmin.affectedUsers",
                                impact.affected_users ?? audience?.count ?? 0,
                              ],
                              [
                                "trainingAdmin.changedCards",
                                (impact.changed?.length || 0) +
                                  (impact.added?.length || 0),
                              ],
                              [
                                "trainingAdmin.carriedCards",
                                impact.unchanged?.length || 0,
                              ],
                              [
                                "trainingAdmin.removedCards",
                                impact.removed?.length || 0,
                              ],
                            ].map(([label, count]) => (
                              <div
                                key={label}
                                className="rounded-xl bg-slate-50 p-3"
                              >
                                <dt className="text-xs text-slate-500">
                                  {t(label)}
                                </dt>
                                <dd className="mt-1 text-2xl font-bold text-slate-900">
                                  {count}
                                </dd>
                              </div>
                            ))}
                          </dl>
                          {[
                            [
                              "trainingAdmin.changedCards",
                              [
                                ...(impact.added || []),
                                ...(impact.changed || []),
                              ],
                            ],
                            [
                              "trainingAdmin.carriedCards",
                              impact.unchanged || [],
                            ],
                            [
                              "trainingAdmin.removedCards",
                              impact.removed || [],
                            ],
                          ]
                            .filter(([, keys]) => keys.length > 0)
                            .map(([label, keys]) => (
                              <details
                                className="rounded-xl border border-slate-200 p-3 text-sm"
                                key={label}
                                open={label === "trainingAdmin.changedCards"}
                              >
                                <summary className="cursor-pointer font-semibold">
                                  {t(label)} ({keys.length})
                                </summary>
                                <ul className="mt-2 list-disc space-y-1 pl-5">
                                  {keys.map((key) => {
                                    const cards = [
                                      ...allCards,
                                      ...(
                                        draft.guide.current_revision?.pages ||
                                        []
                                      ).flatMap((row) => row.cards || []),
                                    ];
                                    const card = cards.find(
                                      (row) => row.key === key,
                                    );
                                    return (
                                      <li key={key}>
                                        {card?.title ||
                                          `${t("trainingAdmin.card")} ${Math.max(1, cards.findIndex((row) => row.key === key) + 1)}`}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </details>
                            ))}
                          {impact.access_issues?.length > 0 && (
                            <div
                              role="alert"
                              className="rounded-xl bg-rose-50 p-4 text-sm text-rose-800"
                            >
                              {t("trainingAdmin.dependencyProblems")}
                              <ul className="mt-2 list-disc pl-4">
                                {impact.access_issues.map((issue, index) => (
                                  <li key={index}>
                                    {typeof issue === "string"
                                      ? issue
                                      : issue.message ||
                                        issue.title ||
                                        `#${issue.user_id}`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="max-h-56 overflow-auto rounded-xl border border-slate-200">
                            <table className="w-full text-left text-sm">
                              <thead>
                                <tr className="bg-slate-50">
                                  <th className="p-3">
                                    {t("trainingAdmin.user")}
                                  </th>
                                  <th className="p-3">
                                    {t("trainingAdmin.positions")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {audience?.data?.map((user) => (
                                  <tr
                                    key={user.id}
                                    className="border-t border-slate-100"
                                  >
                                    <td className="p-3">{user.title}</td>
                                    <td className="p-3">
                                      {user.position ||
                                        t("trainingAdmin.withoutPosition")}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {(audience?.count || 0) > 100 && (
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                className={buttonClass}
                                disabled={busy || !(audience.offset > 0)}
                                onClick={() =>
                                  loadAudiencePage(
                                    Math.max(0, (audience.offset || 0) - 100),
                                  )
                                }
                              >
                                {t("trainingAdmin.previous")}
                              </button>
                              <button
                                type="button"
                                className={buttonClass}
                                disabled={
                                  busy ||
                                  (audience.offset || 0) + 100 >= audience.count
                                }
                                onClick={() =>
                                  loadAudiencePage((audience.offset || 0) + 100)
                                }
                              >
                                {t("trainingAdmin.next")}
                              </button>
                            </div>
                          )}
                          <label className="flex min-h-[44px] items-center gap-3 text-sm">
                            <input
                              type="checkbox"
                              checked={acceptedImpact}
                              onChange={(event) =>
                                setAcceptedImpact(event.target.checked)
                              }
                            />
                            {t("trainingAdmin.acknowledgeImpact")}
                          </label>
                          <button
                            type="button"
                            className={primaryClass}
                            onClick={publish}
                            disabled={
                              !acceptedImpact ||
                              frozen ||
                              Boolean(impact.access_issues?.length)
                            }
                          >
                            {t("trainingAdmin.publishConfirm")}
                          </button>
                        </>
                      )}
                    </section>
                  )}
                </div>
              )}
            </div>
            <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white p-3 pb-[max(.75rem,env(safe-area-inset-bottom))]">
              <div className="text-xs text-slate-500" aria-live="polite">
                {busy
                  ? t("trainingAdmin.saving")
                  : dirty
                    ? t("trainingAdmin.unsaved")
                    : ""}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={buttonClass}
                  onClick={requestClose}
                  disabled={busy}
                >
                  {t("trainingAdmin.close")}
                </button>
                {step > 0 && (
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={() => setStep(step - 1)}
                  >
                    {t("trainingAdmin.previous")}
                  </button>
                )}
                {!published && (
                  <button
                    type="button"
                    className={primaryClass}
                    disabled={frozen || !dirty}
                    onClick={save}
                  >
                    {t("trainingAdmin.saveDraft")}
                  </button>
                )}
                {step < steps.length - 1 && (
                  <button
                    type="button"
                    className={buttonClass}
                    onClick={() => setStep(step + 1)}
                  >
                    {t("trainingAdmin.next")}
                  </button>
                )}
              </div>
            </footer>
          </>
        )}
      </PMDialog>
      {jsonOpen && (
        <TrainingGuideJsonDialog
          draft={draft}
          disabled={frozen || published}
          onClose={() => setJsonOpen(false)}
          onApply={(content, mode) => {
            if (frozen || published) return;
            const next = applyTrainingGuideContent(draft, content, mode);
            setPageIndex(mode === "append" ? draft.revision.pages.length : 0);
            setDraft(next);
            setImpact(null);
            setAudience(null);
            setAcceptedImpact(false);
            setValidation([]);
            setError(null);
            setMessage(t("trainingAdmin.jsonApplied"));
            setStep(1);
            setJsonOpen(false);
          }}
        />
      )}
      {previewData && (
        <TrainingPlayer
          open
          preview
          previewData={{ ...previewData, viewport: previewSize }}
          onClose={() => setPreviewData(null)}
          contextKey={contextKey}
        />
      )}
    </>
  );
}
